import {useCallback, useEffect, useRef, useState} from "react";

// Header behaviour that used to come from jQuery (the removed public/assets/js/scripts.js) and
// Bootstrap 4 JS (data-toggle="dropdown"). Shared by Header.js and HeaderBE.js.

const STICKY_CLASSES = [
    [120, "navbar-sticky-moved-up"],
    [250, "navbar-sticky-transitioned"],
    [500, "navbar-sticky-on"],
];

export default function useHeaderBehaviour() {
    const headerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // Sticky header: same scroll thresholds and classes as before
    useEffect(() => {
        const updateSticky = () => {
            const header = headerRef.current;
            if (!header) return;
            const scroll = window.scrollY;
            STICKY_CLASSES.forEach(([threshold, className]) =>
                header.classList.toggle(className, scroll >= threshold)
            );
        };
        updateSticky();
        window.addEventListener("scroll", updateSticky, {passive: true});
        return () => window.removeEventListener("scroll", updateSticky);
    }, []);

    // Mobile menu toggle. The menu state lives in DOM classes because closeMenu() and
    // App.switchLanguage() close it the same way.
    const toggleMenu = useCallback(() => {
        const header = headerRef.current;
        if (!header) return;
        header.classList.toggle("active");
        document.body.classList.toggle("canvas-open");
        const icon = header.querySelector(".navbar-toggler-icon");
        if (icon) icon.classList.toggle("active");
    }, []);

    // "Meer" dropdown, like Bootstrap: closes on any click outside the toggle
    // (including clicking an item) and on Escape.
    const toggleDropdown = useCallback(() => setDropdownOpen((open) => !open), []);

    useEffect(() => {
        if (!dropdownOpen) return undefined;
        const onClick = (event) => {
            const toggle = dropdownRef.current && dropdownRef.current.querySelector(".dropdown-toggle");
            if (toggle && toggle.contains(event.target)) return;
            setDropdownOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") setDropdownOpen(false);
        };
        document.addEventListener("click", onClick);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("click", onClick);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [dropdownOpen]);

    return {headerRef, dropdownRef, dropdownOpen, toggleDropdown, toggleMenu};
}
